import {FC, useMemo} from "react";
import {Box, LinearProgress, MenuItem, Switch, TextField, Tooltip, Stack} from "@mui/material";
import {
	DataGridPro,
	GridInitialState,
	GridColDef,
	GridFilterInputValueProps,
	GridFilterOperator,
	GRID_CHECKBOX_SELECTION_COL_DEF
} from "@mui/x-data-grid-pro";
import {makeStyles} from "tss-react/mui";
import {PlaylistEnrollmentsGridToolbar} from "./Toolbar/PlaylistEnrollmentsGridToolbar";
import {usePlaylistEnrollmentsGrid} from "./hooks/usePlaylistEnrollmentsGrid";
import {PlaylistEnrollmentDto} from "@/app2/shared/data";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import type {GetCohortSettingsResponse} from "@/app2/shared/data/gen";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";
import {serverSideDateOnlyFilterOperators} from "@/app2/shared/dataGrid/serverSideDateTimeColumnFilters";

const EnrolledFilterInput = (props: GridFilterInputValueProps) => {
	const {item, applyValue, focusElementRef} = props;
	return (
		<TextField
			select
			variant="standard"
			label="Value"
			InputLabelProps={{shrink: true}}
			SelectProps={{displayEmpty: true}}
			value={item.value ?? ""}
			onChange={(e) => applyValue({...item, value: e.target.value})}
			inputRef={focusElementRef}
		>
			<MenuItem value="true">true</MenuItem>
			<MenuItem value="false">false</MenuItem>
		</TextField>
	);
};

const enrolledFilterOperators: GridFilterOperator[] = [{
	value: "is",
	getApplyFilterFn: () => null,
	InputComponent: EnrolledFilterInput
}];

const useStyles = makeStyles()((theme) => ({
	dataGrid: {
		padding: 0,
		border: 0,
		"& .MuiDataGrid-columnHeaders": {
			borderBottom: `1px solid ${theme.palette.grey[200]}`
		},
		"& .MuiDataGrid-cell": {
			borderBottom: `1px solid ${theme.palette.grey[100]}`
		}
	},
	progressBarRoot: {
		width: "100%",
		alignItems: "center",
		gap: theme.spacing(2)
	},
	progressBar: {
		width: "100%"
	}
}));

interface PlaylistEnrollmentsGridProps {
	playlistId: number;
	cohortId: number;
	organization?: GetCohortSettingsResponse | null;
	isPlaylistPublished: boolean;
	selected: number[];
	setSelected: (ids: number[]) => void;
	setSelectedSingular: (id: number | undefined) => void;
	handleEnroll: (userIds: number[]) => void;
	handleUnenroll: (userIds: number[]) => void;
	handleMarkComplete: (userIds: number[]) => void;
}

export const PlaylistEnrollmentsGrid: FC<PlaylistEnrollmentsGridProps> = ({
	playlistId,
	cohortId,
	organization,
	isPlaylistPublished,
	selected,
	setSelected,
	setSelectedSingular,
	handleEnroll,
	handleUnenroll,
	handleMarkComplete
}) => {
	const {classes} = useStyles();
	const {t} = useSSTranslation();

	const {
		enrollments,
		rowCount,
		isFetching,
		page,
		pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		sortModel,
		onSortModelChange,
		onRowSelectionChange,
		defaultInitialState
	} = usePlaylistEnrollmentsGrid({
		playlistId,
		cohortId,
		selected,
		setSelected
	});


	const columns: GridColDef<PlaylistEnrollmentDto>[] = useMemo(() => {
		const baseColumns: GridColDef<PlaylistEnrollmentDto>[] = [
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				type: "string",
				disableColumnMenu: true
			},
			{
				field: "firstname",
				flex: 1,
				headerName: t("common_first_name", "First Name"),
				type: "string"
			},
			{
				field: "lastname",
				flex: 1,
				headerName: t("common_last_name", "Last Name"),
				type: "string"
			},
			{
				field: "employeeCode",
				flex: 1,
				headerName: t("common_employee_code", "Employee Code"),
				type: "string"
			},
			{
				field: "email",
				flex: 1,
				headerName: t("common_email", "Email"),
				type: "string"
			},
			{
				field: "jobCategory",
				flex: 1,
				headerName: t("common_job_category", "Job Category"),
				type: "string"
			},
			{
				field: "jobTitle",
				flex: 1,
				headerName: t("common_job_title", "Job Title"),
				type: "string"
			},
			{
				field: "hireDate",
				flex: 1,
				headerName: t("common_hire_date", "Hire Date"),
				type: "date",
				filterOperators: serverSideDateOnlyFilterOperators,
				valueGetter: (params: any) =>
					params.row.hireDate ? new Date(params.row.hireDate) : null,
				valueFormatter: (params: any) => {
					if (!params.value) return "";
					return formatDateForDisplay(params.value as Date, {timeZone: "UTC"});
				}
			},
			{
				field: "titleStartDate",
				flex: 1,
				headerName: t("common_title_start_date", "Title Start Date"),
				type: "date",
				filterOperators: serverSideDateOnlyFilterOperators,
				valueGetter: (params: any) =>
					params.row.titleStartDate ? new Date(params.row.titleStartDate) : null,
				valueFormatter: (params: any) => {
					if (!params.value) return "";
					return formatDateForDisplay(params.value as Date, {timeZone: "UTC"});
				}
			},
			{
				field: "enrolled",
				flex: 1,
				headerName: t("common_enroll", "Enroll"),
				type: "boolean",
				filterOperators: enrolledFilterOperators,
				renderCell: (params: any) => {
					return (
						<Tooltip
							title={
								!isPlaylistPublished
									? t(
										"playlist_draft_not_enrollment_description",
										"Please publish the playlist to enable user enrollments.",
									)
									: ""
							}
						>
							<span>
								<Switch
									checked={params.value as boolean}
									disabled={!isPlaylistPublished}
									onClick={(e) => {
										e.stopPropagation();
										setSelectedSingular(params.row.id);
										if (params.value) {
											handleUnenroll([params.row.id]);
										} else {
											handleEnroll([params.row.id]);
										}
									}}
								/>
							</span>
						</Tooltip>
					);
				}
			},
			{
				field: "assignedOn",
				flex: 1,
				headerName: t("common_assigned_on", "Assigned On"),
				type: "date",
				filterOperators: serverSideDateOnlyFilterOperators,
				valueFormatter: (params: any) => {
					if (!params.value) return "";
					return formatDateForDisplay(params.value as Date | string, {timeZone: "UTC"});
				}
			},
			{
				field: "enrollmentType",
				flex: 1,
				headerName: t("common_enrollment_type", "Enrollment Type"),
				type: "string",
				valueGetter: (params: any) =>
					params.value ? t(`user_enrollment_type_${params.value}`, params.value as string) : ""
			},
			{
				field: "progress",
				flex: 1,
				headerName: t("common_playlist_completion", "Playlist Completion"),
				type: "number",
				renderCell: (params: any) => {
					return (
						<Stack className={classes.progressBarRoot} direction="row">
							<LinearProgress
								className={classes.progressBar}
								variant="determinate"
								value={params.value ?? 0}
							/>
							<span>{`${params.value ?? 0}%`}</span>
						</Stack>
					);
				}
			},
			{
				field: "division",
				flex: 1,
				type: "string",
				headerName: t("common_division", "Division")
			},
			{
				field: "supervisorName",
				flex: 1,
				type: "string",
				headerName: t("common_supervisor", "Supervisor")
			}
		];

		if (organization?.isActiveCustomField1) {
			baseColumns.push({
				field: "customFieldValue1",
				headerName: String(organization.customFieldName1 ?? "Custom Field 1"),
				flex: 1,
				type: "string"
			});
		}
		if (organization?.isActiveCustomField2) {
			baseColumns.push({
				field: "customFieldValue2",
				headerName: String(organization.customFieldName2 ?? "Custom Field 2"),
				flex: 1,
				type: "string"
			});
		}
		if (organization?.isActiveCustomField3) {
			baseColumns.push({
				field: "customFieldValue3",
				headerName: String(organization.customFieldName3 ?? "Custom Field 3"),
				flex: 1,
				type: "string"
			});
		}

		return baseColumns;
	}, [isPlaylistPublished, setSelectedSingular, handleEnroll, handleUnenroll, t, classes, organization]);

	return (
		<Box>
			<DataGridPro<PlaylistEnrollmentDto>
				key={`playlist-grid-${playlistId}-${page}-${pageSize}`} // Add key to force re-render
				autoHeight
				className={classes.dataGrid}
				loading={isFetching}
				sortingMode="server"
				page={page}
				keepNonExistentRowsSelected
				pageSize={pageSize}
				onPageSizeChange={onPageSizeChange}
				onPageChange={onPageChange}
				checkboxSelection
				onSelectionModelChange={onRowSelectionChange}
				paginationMode="server"
				pagination
				rowCount={rowCount}
				sortModel={sortModel}
				onSortModelChange={onSortModelChange}
				rows={enrollments}
				columns={columns}
				filterMode="server"
				onFilterModelChange={onFilterChange}
				components={{Toolbar: PlaylistEnrollmentsGridToolbar}}
				componentsProps={{
					toolbar: {
						selectedRows: selected,
						isPlaylistPublished,
						handleEnroll,
						handleUnenroll,
						handleMarkComplete
					}
				}}
				rowsPerPageOptions={[25, 50, 100, 200]}
				initialState={defaultInitialState}
			/>
		</Box>
	);
};
